/**
 * Capacitor utilities for native mobile features
 * These are only active when running inside the Capacitor native shell
 */

import { BREAKPOINTS } from "@/lib/breakpoints";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";

/**
 * Get the original (unpatched) window.fetch for SSE streaming.
 * CapacitorHttp patches window.fetch to route through native networking,
 * which buffers entire responses and can't handle ReadableStream.
 * The original is saved in index.html via an inline <script> that runs
 * before Capacitor loads — stored as window.__originalFetch.
 */
export function getOriginalFetch(): typeof fetch {
  return typeof window !== "undefined" && (window as any).__originalFetch
    ? (window as any).__originalFetch
    : fetch;
}
import {
  PushNotifications,
  type Token,
  type PushNotificationSchema,
  type ActionPerformed,
} from "@capacitor/push-notifications";
import { Keyboard } from "@capacitor/keyboard";
import { Haptics, ImpactStyle, NotificationType } from "@capacitor/haptics";

/**
 * Check if running inside Capacitor native app
 */
export function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Get native app version and build number (iOS/Android only)
 * Returns null on web
 */
export async function getAppInfo(): Promise<{
  version: string;
  build: string;
} | null> {
  if (!isNative()) return null;
  try {
    const info = await App.getInfo();
    return { version: info.version, build: info.build };
  } catch {
    return null;
  }
}

/**
 * Get the current platform
 */
export function getPlatform(): "ios" | "android" | "web" {
  return Capacitor.getPlatform() as "ios" | "android" | "web";
}

// ============================================
// PUSH NOTIFICATIONS
// ============================================

type PushNotificationHandler = (notification: PushNotificationSchema) => void;
type PushActionHandler = (action: ActionPerformed) => void;

let pushRegistered = false;
const notificationHandlers: PushNotificationHandler[] = [];
let actionHandlers: PushActionHandler[] = [];

/**
 * Reset push registration state (call when user disables notifications)
 * so that re-enabling does a clean re-registration with fresh listeners
 */
export function resetPushRegistration(): void {
  pushRegistered = false;
  (window as any).__pushToken = null;
  // Remove all existing push listeners so they don't accumulate
  PushNotifications.removeAllListeners().catch(() => {});
}

/**
 * Register for push notifications and get the device token
 */
export async function registerPushNotifications(): Promise<string | null> {
  if (!isNative()) {
    console.log("[push] Not native platform, skipping registration");
    return null;
  }

  // If already registered, return existing token
  if (pushRegistered && (window as any).__pushToken) {
    return (window as any).__pushToken;
  }

  try {
    // Request permission
    const permStatus = await PushNotifications.requestPermissions();
    console.log("[push] Permission status:", permStatus.receive);

    if (permStatus.receive !== "granted") {
      console.log(
        "[push] Permission not granted — user may need to enable in iOS Settings",
      );
      // Throw specific error so caller can show appropriate message
      throw new Error("PERMISSION_DENIED");
    }

    // Create a promise that resolves when we get the token
    const tokenPromise = new Promise<string | null>((resolve) => {
      const timeout = setTimeout(() => {
        console.log("[push] Token timeout - no token received");
        resolve(null);
      }, 10000); // 10 second timeout

      PushNotifications.addListener("registration", (token: Token) => {
        clearTimeout(timeout);
        console.log(
          "[push] Device token received:",
          token.value?.substring(0, 20) + "...",
        );
        (window as any).__pushToken = token.value;
        resolve(token.value);
      });

      PushNotifications.addListener("registrationError", (error) => {
        clearTimeout(timeout);
        console.error("[push] Registration error:", error);
        resolve(null);
      });
    });

    // Set up other listeners (only once)
    if (!pushRegistered) {
      PushNotifications.addListener(
        "pushNotificationReceived",
        (notification) => {
          console.log("[push] Notification received:", notification);
          notificationHandlers.forEach((handler) => handler(notification));
        },
      );

      PushNotifications.addListener(
        "pushNotificationActionPerformed",
        (action) => {
          console.log("[push] Action performed:", action);
          actionHandlers.forEach((handler) => handler(action));
        },
      );
    }

    // Register with APNs/FCM
    console.log("[push] Calling PushNotifications.register()...");
    await PushNotifications.register();
    console.log("[push] Register called, waiting for token...");

    pushRegistered = true;

    // Wait for the token
    const token = await tokenPromise;
    return token;
  } catch (error) {
    // Re-throw PERMISSION_DENIED so callers can show the right message
    if (error instanceof Error && error.message === "PERMISSION_DENIED") {
      throw error;
    }
    console.error("[push] Failed to register:", error);
    return null;
  }
}

/**
 * Get the current push notification token
 */
export function getPushToken(): string | null {
  return (window as any).__pushToken || null;
}

/**
 * Add a handler for push notification actions (user tapped notification)
 */
export function onPushAction(handler: PushActionHandler): () => void {
  actionHandlers.push(handler);
  return () => {
    actionHandlers = actionHandlers.filter((h) => h !== handler);
  };
}

// ============================================
// KEYBOARD HANDLING
// ============================================

type KeyboardShowHandler = (info: { keyboardHeight: number }) => void;
type KeyboardHideHandler = () => void;

let keyboardShowHandlers: KeyboardShowHandler[] = [];
const keyboardHideHandlers: KeyboardHideHandler[] = [];
let keyboardListenersInitialized = false;
let webKeyboardInitialized = false;

/**
 * Initialize keyboard detection for mobile web browsers using the Visual Viewport API.
 * This runs on all mobile browsers (not native Capacitor) and sets the same
 * body.keyboard-open class and --keyboard-height CSS variable that the native
 * Capacitor keyboard plugin uses.
 */
export function initWebKeyboardDetection(): void {
  if (typeof window === "undefined" || !window.visualViewport) return;
  if (isNative()) return;
  if (webKeyboardInitialized) return;

  webKeyboardInitialized = true;
  let wasKeyboardOpen = false;

  window.visualViewport.addEventListener("resize", () => {
    // Only activate on mobile-width viewports; on desktop the difference
    // stays near 0 anyway, but skipping avoids any browser-zoom edge cases.
    if (window.innerWidth > BREAKPOINTS.compactMax) return;

    const currentHeight = window.visualViewport!.height;
    const keyboardHeight = window.innerHeight - currentHeight;

    if (keyboardHeight > 100) {
      document.body.classList.add("keyboard-open");
      document.documentElement.style.setProperty(
        "--keyboard-height",
        `${keyboardHeight}px`,
      );
      wasKeyboardOpen = true;
    } else if (keyboardHeight < 50 && wasKeyboardOpen) {
      document.body.classList.remove("keyboard-open");
      document.documentElement.style.setProperty("--keyboard-height", "0px");
      wasKeyboardOpen = false;
    }
  });
}

/**
 * Initialize keyboard listeners (Capacitor native only)
 */
export function initKeyboardListeners(): void {
  if (!isNative() || keyboardListenersInitialized) return;

  console.log("[keyboard] Initializing keyboard listeners...");

  const handleKeyboardShow = (info: { keyboardHeight: number }) => {
    console.log("[keyboard] SHOW - height:", info.keyboardHeight);
    document.body.classList.add("keyboard-open");
    document.documentElement.style.setProperty(
      "--keyboard-height",
      `${info.keyboardHeight}px`,
    );
    keyboardShowHandlers.forEach((handler) =>
      handler({ keyboardHeight: info.keyboardHeight }),
    );

    // Scroll focused element into view after keyboard animation
    requestAnimationFrame(() => {
      const activeElement = document.activeElement;
      if (
        activeElement instanceof HTMLElement &&
        (activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA")
      ) {
        activeElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    });
  };

  const handleKeyboardHide = () => {
    console.log("[keyboard] HIDE");
    document.body.classList.remove("keyboard-open");
    document.documentElement.style.setProperty("--keyboard-height", "0px");
    keyboardHideHandlers.forEach((handler) => handler());
  };

  // iOS uses keyboardWillShow/Hide, Android may need keyboardDidShow/Hide
  Keyboard.addListener("keyboardWillShow", handleKeyboardShow);
  Keyboard.addListener("keyboardDidShow", handleKeyboardShow);
  Keyboard.addListener("keyboardWillHide", handleKeyboardHide);
  Keyboard.addListener("keyboardDidHide", handleKeyboardHide);

  // Fallback: Use visualViewport API for iOS (more reliable)
  if (window.visualViewport) {
    console.log("[keyboard] Setting up visualViewport fallback");
    let wasKeyboardOpen = false;

    window.visualViewport.addEventListener("resize", () => {
      const currentHeight = window.visualViewport!.height;
      const keyboardHeight = window.innerHeight - currentHeight;

      console.log(
        "[keyboard] visualViewport resize - viewport:",
        currentHeight,
        "keyboard:",
        keyboardHeight,
      );

      if (keyboardHeight > 100) {
        // Keyboard is likely open (threshold to avoid false positives)
        document.body.classList.add("keyboard-open");
        document.documentElement.style.setProperty(
          "--keyboard-height",
          `${keyboardHeight}px`,
        );
        // Call show handlers if keyboard just opened
        if (!wasKeyboardOpen) {
          keyboardShowHandlers.forEach((handler) =>
            handler({ keyboardHeight }),
          );
        }
        wasKeyboardOpen = true;
      } else if (keyboardHeight < 50) {
        // Keyboard is likely closed
        document.body.classList.remove("keyboard-open");
        document.documentElement.style.setProperty("--keyboard-height", "0px");
        // Call hide handlers if keyboard just closed
        if (wasKeyboardOpen) {
          keyboardHideHandlers.forEach((handler) => handler());
        }
        wasKeyboardOpen = false;
      }
    });
  }

  console.log("[keyboard] Listeners registered");
  keyboardListenersInitialized = true;
}

/**
 * Add a handler for keyboard show
 */
export function onKeyboardShow(handler: KeyboardShowHandler): () => void {
  keyboardShowHandlers.push(handler);
  return () => {
    keyboardShowHandlers = keyboardShowHandlers.filter((h) => h !== handler);
  };
}

// ============================================
// HAPTICS
// ============================================

/**
 * Trigger a light haptic impact
 */
export async function hapticLight(): Promise<void> {
  if (!isNative()) return;
  try {
    await Haptics.impact({ style: ImpactStyle.Light });
  } catch (e) {
    // Ignore errors
  }
}

/**
 * Trigger a medium haptic impact
 */
export async function hapticMedium(): Promise<void> {
  if (!isNative()) return;
  try {
    await Haptics.impact({ style: ImpactStyle.Medium });
  } catch (e) {
    // Ignore errors
  }
}

/**
 * Trigger a success haptic notification
 */
export async function hapticSuccess(): Promise<void> {
  if (!isNative()) return;
  try {
    await Haptics.notification({ type: NotificationType.Success });
  } catch (e) {
    // Ignore errors
  }
}

/**
 * Trigger an error haptic notification
 */
export async function hapticError(): Promise<void> {
  if (!isNative()) return;
  try {
    await Haptics.notification({ type: NotificationType.Error });
  } catch (e) {
    // Ignore errors
  }
}
